import React, { useCallback, useEffect, useMemo, useState } from 'react';



import { OrderStatus, PersistedOrderPayload, ThemeColors, MenuItem, FontConfig, ZoneColors } from '@onesunday/domain';



import {



  fetchOrders,



  updateOrderStatus,



  fetchPaymentRecon,

  fetchAppSettings,

  updateAppSettings,

  uploadFlyer,

  getAdminApiKey,

  setAdminApiKey as persistAdminApiKey,

  clearAdminApiKey,



  AppSettingsResponse,

  UpdateAppSettingsPayload,

  PaymentReconRow,

  FlyerExtracted,

} from '@onesunday/api-client';



import { endOfDay, endOfMonth, format, parseISO, startOfDay, startOfMonth } from 'date-fns';



import { RefreshCw, AlertCircle } from 'lucide-react';



import { FilterBar } from './components/FilterBar';



import { OrderList } from './components/OrderList';



import { OrderDrawer } from './components/OrderDrawer';



import { InsightsTab } from './components/InsightsTab';

import { CustomerEmailModal, EmailScope } from './components/CustomerEmailModal';



import { CountMode, GraphFilter } from './types';



import {



  getOrderHourLabel,



  getOrderTargetDate,



  statusPriority,



  CAMPAIGN_DETAILS,



} from './constants';







const currency = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' });







type SortField = 'orderId' | 'customer' | 'arrival' | 'status' | 'createdAt';



type SortDirection = 'asc' | 'desc';







const ALLOWED_STATUSES: OrderStatus[] = ['Paid', 'Ready', 'Completed', 'Late'];



const DEFAULT_STATUS_FILTERS: StatusFilter[] = ['Paid', 'Ready', 'Completed', 'Late'];







const isTestOrder = (order: PersistedOrderPayload) =>



  order.yocoTestMode === true || order.yocoTestMode === 'true';



type StatusFilter = OrderStatus | 'Late';



type HourFilter = string;



type ActiveTab = 'orders' | 'payments' | 'settings' | 'insights';

const getCurrentMonthValue = () => format(new Date(), 'yyyy-MM');

const getMonthRange = (monthValue: string) => {
  const parsed = parseISO(`${monthValue}-01`);
  const baseDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return {
    from: format(startOfMonth(baseDate), 'yyyy-MM-dd'),
    to: format(endOfMonth(baseDate), 'yyyy-MM-dd'),
  };
};

const toIsoStart = (dateValue: string) => `${dateValue}T00:00:00.000Z`;
const toIsoEnd = (dateValue: string) => `${dateValue}T23:59:59.999Z`;
const DEFAULT_PICKUP_ADDRESS = '1 Drakenstein Avenue, Paulshof';
const DEFAULT_PICKUP_HOURS = '14:00–18:00 (2–6pm)';
const DEFAULT_PICKUP_CONTACT = '082 267 8913';

const inSelectedRange = (order: PersistedOrderPayload, fromDate: Date | null, toDate: Date | null) => {
  const createdAt = order.createdAt || order.timestamp;
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return false;
  if (fromDate && createdDate < fromDate) return false;
  if (toDate && createdDate > toDate) return false;
  return true;
};







const DEFAULT_THEME_COLORS: ThemeColors = {
  teal: '#C9757E', orange: '#E2B79A', red: '#8D4A3A',
  yellow: '#D4B35A', dark: '#191411', cream: '#F5EFE5',
};

const applyThemeColors = (colors: ThemeColors) => {
  const root = document.documentElement;
  root.style.setProperty('--color-flyer-teal',   colors.teal);
  root.style.setProperty('--color-flyer-orange', colors.orange);
  root.style.setProperty('--color-flyer-red',    colors.red);
  root.style.setProperty('--color-flyer-yellow', colors.yellow);
  root.style.setProperty('--color-flyer-dark',   colors.dark);
  root.style.setProperty('--color-flyer-cream',  colors.cream);
};

const DEFAULT_FONT_CONFIG: FontConfig = { display: 'Stardos Stencil', body: 'Manrope' };

const DISPLAY_FONTS: { name: string; label: string; description: string }[] = [
  { name: 'Stardos Stencil', label: 'Stencil',        description: 'Bold military stencil — events & posters (default)' },
  { name: 'Playfair Display', label: 'Playfair',       description: 'Elegant serif — upscale dining & formal events' },
  { name: 'Bebas Neue',       label: 'Bebas Neue',     description: 'Condensed modern — urban & minimal' },
  { name: 'Abril Fatface',    label: 'Abril Fatface',  description: 'Editorial bold — festive & colourful flyers' },
  { name: 'Righteous',        label: 'Righteous',      description: 'Retro groovy — vintage & playful' },
];

const BODY_FONTS: { name: string; label: string; description: string }[] = [
  { name: 'Manrope',  label: 'Manrope',  description: 'Geometric humanist — clean & modern (default)' },
  { name: 'Inter',    label: 'Inter',    description: 'Neutral technical — precise & minimal' },
  { name: 'Lato',     label: 'Lato',     description: 'Warm humanist — friendly & approachable' },
  { name: 'Raleway',  label: 'Raleway',  description: 'Elegant thin — refined & fashion-forward' },
  { name: 'Nunito',   label: 'Nunito',   description: 'Rounded — soft & casual' },
];

const applyFontConfig = (config: FontConfig) => {
  const root = document.documentElement;
  root.style.setProperty('--font-display', `'${config.display}'`);
  root.style.setProperty('--font-body',    `'${config.body}'`);
  const families = [
    `${config.display.replace(/ /g, '+')}:wght@400;700`,
    `${config.body.replace(/ /g, '+')}:wght@400;500;600;700;800`,
  ].join('&family=');
  const id = 'dynamic-fonts';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
  link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
};

/**
 * Suggest a font pairing based on image mood derived from HSL pixel data.
 * No ML required — uses average saturation, dominant hue warmth, and contrast.
 */
const suggestFontsFromPixels = (pixels: [number, number, number][]): FontConfig => {
  if (pixels.length === 0) return DEFAULT_FONT_CONFIG;
  const avgSat = pixels.reduce((s, [, sat]) => s + sat, 0) / pixels.length;
  const warmPixels = pixels.filter(([h]) => h >= 15 && h <= 70);
  const coolPixels = pixels.filter(([h]) => h >= 180 && h <= 300);
  const sorted = [...pixels].sort((a, b) => a[2] - b[2]);
  const contrast = sorted[Math.floor(sorted.length * 0.95)]?.[2] - sorted[Math.floor(sorted.length * 0.05)]?.[2];
  const isWarm  = warmPixels.length > pixels.length * 0.3;
  const isCool  = coolPixels.length > pixels.length * 0.3;
  const isMono  = avgSat < 0.12;
  const isFestive = avgSat > 0.35 && !isWarm && !isCool;

  if (isMono && contrast > 0.65) return { display: 'Bebas Neue',       body: 'Inter' };
  if (isCool && avgSat > 0.25)   return { display: 'Playfair Display', body: 'Raleway' };
  if (isFestive)                  return { display: 'Abril Fatface',    body: 'Nunito' };
  if (isWarm && avgSat > 0.40)   return { display: 'Bebas Neue',       body: 'Lato' };
  return DEFAULT_FONT_CONFIG; // warm medium-sat — typical event poster
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

import { extractFlyerInfoOcr } from './utils/flyerParser';

// ── Canvas-based palette extraction ────────────────────────────────────────
//
// Design contract — each colour slot has a fixed legibility role:
//
//   dark   → page background      (white/cream text on it)  L ≤ 16%
//   cream  → card/modal surface   (dark text on it)         L ≥ 87%
//   teal   → primary header bar   (white text on it)        L 28–50%  S ≥ 42%
//   red    → CTA / action button  (white text on it)        L 22–44%  S ≥ 42%
//   yellow → Add-to-Cart button   (dark text on it)         L 52–68%  S ≥ 35%
//   orange → hover / warm accent  (dark text on it)         L 58–74%  S ≥ 22%
//
// The HUE is taken from the image; the LIGHTNESS is enforced by role.
// This guarantees WCAG-safe contrast regardless of what the flyer looks like.

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r1) h = ((g1 - b1) / d + (g1 < b1 ? 6 : 0)) / 6;
  else if (max === g1) h = ((b1 - r1) / d + 2) / 6;
  else h = ((r1 - g1) / d + 4) / 6;
  return [h * 360, s, l];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) { const v = Math.round(l * 255).toString(16).padStart(2, '0'); return `#${v}${v}${v}`; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const rr = Math.round(hue2rgb(p, q, h / 360 + 1 / 3) * 255);
  const gg = Math.round(hue2rgb(p, q, h / 360) * 255);
  const bb = Math.round(hue2rgb(p, q, h / 360 - 1 / 3) * 255);
  return `#${rr.toString(16).padStart(2, '0')}${gg.toString(16).padStart(2, '0')}${bb.toString(16).padStart(2, '0')}`;
};

/** Circular hue distance (degrees) */
const hueDist = (a: number, b: number) => Math.min(Math.abs(a - b), 360 - Math.abs(a - b));

/** Parse a 6-digit hex colour string into HSL */
const hexToHsl = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return rgbToHsl(r, g, b);
};

/**
 * Extract N perceptually-distinct dominant colours from a pixel array using
 * furthest-point greedy selection weighted by vibrancy (saturation × mid-L).
 */
const extractDominantColors = (pixels: [number, number, number][], n: number): string[] => {
  // Include near-neutrals (s > 0.04) so B&W/monochrome flyers produce real swatches.
  // Exclude only pure blacks/whites (l < 0.06 or l > 0.94) that add no useful information.
  const pool = pixels.filter(([, s, l]) => s > 0.04 && l > 0.06 && l < 0.94);
  if (pool.length === 0) return [];
  const vibrancy = ([, s, l]: [number, number, number]) => s * (1 - Math.abs(l - 0.5) * 1.8);
  pool.sort((a, b) => vibrancy(b) - vibrancy(a));
  const colorDist = (a: [number, number, number], b: [number, number, number]) =>
    hueDist(a[0], b[0]) * 0.55 + Math.abs(a[1] - b[1]) * 90 + Math.abs(a[2] - b[2]) * 90;
  const selected: [number, number, number][] = [pool[0]];
  const candidates = pool.slice(0, Math.min(pool.length, 1200));
  while (selected.length < n) {
    let bestDist = -1, bestIdx = -1;
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      if (selected.some(s => colorDist(s, c) < 7)) continue;
      const d = Math.min(...selected.map(s => colorDist(s, c)));
      if (d > bestDist) { bestDist = d; bestIdx = i; }
    }
    if (bestIdx === -1 || bestDist < 5) break;
    selected.push(candidates[bestIdx]);
  }
  return selected.map(([h, s, l]) => hslToHex(h, s, l));
};

/**
 * Generate a rich set of harmonious colours from a single seed hex.
 * Returns an array of { label, hex } — all values are clamped to safe ranges.
 */
const generateHarmonics = (hex: string): { label: string; hex: string }[] => {
  if (!hex || hex.length < 7) return [];
  const [h, s, l] = hexToHsl(hex);
  const cl = (v: number) => Math.max(0.04, Math.min(0.96, v));
  const cs = (v: number) => Math.max(0, Math.min(1, v));
  const go = (dh: number, ds: number, dl: number): string =>
    hslToHex(((h + dh) % 360 + 360) % 360, cs(s + ds), cl(l + dl));
  return [
    { label: 'Tint',        hex: go(0,   -(s * 0.35), +0.20) },
    { label: 'Pale',        hex: go(0,   -(s * 0.55), +0.34) },
    { label: 'Shade',       hex: go(0,   +(s * 0.08), -0.18) },
    { label: 'Deep',        hex: go(0,   +(s * 0.12), -0.38) },
    { label: 'Vivid',       hex: go(0,   +cs(1 - s) * 0.6, 0) },
    { label: 'Complement',  hex: go(180,  0,           0)    },
    { label: 'Split A',     hex: go(150,  0,           0)    },
    { label: 'Split B',     hex: go(210,  0,           0)    },
    { label: 'Triadic A',   hex: go(120,  0,           0)    },
    { label: 'Triadic B',   hex: go(240,  0,           0)    },
    { label: 'Warm',        hex: go(+28, +0.08,        0)    },
    { label: 'Cool',        hex: go(-28, +0.08,        0)    },
  ];
};

/**
 * Score hue buckets (10° each) by count × avg-saturation.
 * Returns top-N bucket centroids — the "dominant" hues in the image.
 */
const dominantHues = (pixels: [number, number, number][], topN: number): number[] => {
  const count = new Array(36).fill(0) as number[];
  const satSum = new Array(36).fill(0) as number[];
  for (const [h, s] of pixels) {
    const bucket = Math.floor(((h % 360) + 360) % 360 / 10);
    count[bucket]++;
    satSum[bucket] += s;
  }
  return Array.from({ length: 36 }, (_, i) => ({
    hue: i * 10 + 5,
    score: count[i] > 0 ? (satSum[i] / count[i]) * count[i] : 0,
  }))
    .sort((a, b) => b.score - a.score)
    .slice(0, topN)
    .map(({ hue }) => hue);
};

/**
 * Find the most-saturated pixel near `hue` (±25°) within a loosened lightness
 * window, then clamp L and S to the role's required range.
 * Falls back to a synthesised colour if no pixel is found.
 */
const sampleAtHue = (
  pixels: [number, number, number][],
  hue: number,
  lMin: number, lMax: number, sMin: number,
): [number, number, number] => {
  const targetL = (lMin + lMax) / 2;
  const nearby = pixels
    .filter(([h, s, l]) => hueDist(h, hue) < 30 && l >= lMin - 0.06 && l <= lMax + 0.06 && s >= sMin * 0.4)
    .sort((a, b) => {
      const scoreA = a[1] * (1 - Math.abs(a[2] - targetL) * 1.5);
      const scoreB = b[1] * (1 - Math.abs(b[2] - targetL) * 1.5);
      return scoreB - scoreA;
    });
  const [h, s, l] = nearby[0] ?? [hue, sMin, targetL];
  return [h, Math.max(sMin, Math.min(1, s)), Math.max(lMin, Math.min(lMax, l))];
};

/**
 * Role constraints for the 4 accent slots.
 * hMin/hMax define the *preferred* hue range for each role — the extractor
 * tries this range first before falling back to hue-separation rules.
 * When hMax < hMin the range wraps through 0° (e.g. red: 340°→30°).
 */
const ACCENT_SLOTS: ReadonlyArray<{
  key: keyof ThemeColors;
  lMin: number; lMax: number; sMin: number;
  hMin: number; hMax: number;
}> = [
  { key: 'teal',   lMin: 0.35, lMax: 0.65, sMin: 0.50, hMin: 155, hMax: 220 }, // cyan/teal sky
  { key: 'red',    lMin: 0.38, lMax: 0.65, sMin: 0.55, hMin: 340, hMax:  30 }, // red/coral (wraps 0°)
  { key: 'orange', lMin: 0.50, lMax: 0.72, sMin: 0.45, hMin:  15, hMax:  55 }, // warm orange
  { key: 'yellow', lMin: 0.55, lMax: 0.78, sMin: 0.38, hMin:  50, hMax:  85 }, // yellow/amber
];

/** True when hue h lies within [hMin, hMax], wrapping through 0° when hMax < hMin */
const inHuePref = (h: number, hMin: number, hMax: number): boolean =>
  hMin <= hMax ? h >= hMin && h <= hMax : h >= hMin || h <= hMax;

const extractPaletteFromFile = (file: File): Promise<ThemeColors> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const W = 100, H = 100;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(DEFAULT_THEME_COLORS); return; }
      ctx.drawImage(img, 0, 0, W, H);
      const { data } = ctx.getImageData(0, 0, W, H);

      // ── Collect opaque pixels as HSL ──────────────────────────────────────
      const all: [number, number, number][] = [];
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 128) continue;
        all.push(rgbToHsl(data[i], data[i + 1], data[i + 2]));
      }
      if (all.length === 0) { resolve(DEFAULT_THEME_COLORS); return; }
      all.sort((a, b) => a[2] - b[2]); // sort by lightness ascending

      // ── Chromatic pool (exclude near-greys, fallback to all) ──────────────
      const chromatic = all.filter(([, s]) => s > 0.12);
      const accentPool = chromatic.length >= 40 ? chromatic : all;
      const avgSat = accentPool.reduce((sum, [, s]) => sum + s, 0) / accentPool.length;

      // ── DARK: page background ─────────────────────────────────────────────
      // P06 by lightness, clamped to L ≤ 16% → always readable with cream text
      const darkRaw = all[Math.floor(all.length * 0.06)];
      const dark: [number, number, number] = [darkRaw[0], darkRaw[1], Math.min(darkRaw[2], 0.16)];

      // ── CREAM: light surface ──────────────────────────────────────────────
      // P94 by lightness, clamped to L ≥ 87%, heavily desaturated so it reads
      // as a neutral card background rather than a tinted surface
      const lightRaw = all[Math.floor(all.length * 0.94)];
      const cream: [number, number, number] = [lightRaw[0], lightRaw[1] * 0.20, Math.max(lightRaw[2], 0.87)];

      // ── Accent assignment ─────────────────────────────────────────────────
      // Pick from dominant hues with ≥ 30° separation; sample image pixels at
      // that hue then clamp to each slot's required lightness band.
      const topHues = dominantHues(accentPool, 12);
      const assignedHues: number[] = [];
      const accents: Partial<Record<keyof ThemeColors, string>> = {};

      for (const slot of ACCENT_SLOTS) {
        // 1. Prefer a hue that falls within this slot's semantic hue range AND
        //    hasn't been assigned yet with enough separation.
        // 2. Fall back to normal hue-separation rules.
        // 3. Last resort: any unassigned hue.
        const chosen =
          topHues.find(h => inHuePref(h, slot.hMin, slot.hMax) && assignedHues.every(ah => hueDist(h, ah) >= 20)) ??
          topHues.find(h => inHuePref(h, slot.hMin, slot.hMax) && !assignedHues.includes(h)) ??
          topHues.find(h => assignedHues.every(ah => hueDist(h, ah) >= 30)) ??
          topHues.find(h => assignedHues.every(ah => hueDist(h, ah) >= 20)) ??
          topHues.find(h => !assignedHues.includes(h)) ??
          topHues[0] ?? 0;
        assignedHues.push(chosen);
        const [h, s, l] = sampleAtHue(accentPool, chosen, slot.lMin, slot.lMax, slot.sMin);
        accents[slot.key] = hslToHex(h, s, l);
      }

      // ── Monochromatic fallback ────────────────────────────────────────────
      // If the image has almost no colour, synthesise 4 hue-diverse accents
      // from the dominant hue at fixed 72° intervals so the UI stays viable.
      if (avgSat < 0.10) {
        const base = dark[0];
        const offsets = [0, 72, 150, 222];
        const monoSlots: [keyof ThemeColors, number, number][] = [
          ['teal', 0.39, 0.42], ['red', 0.34, 0.42],
          ['yellow', 0.62, 0.50], ['orange', 0.66, 0.40],
        ];
        monoSlots.forEach(([key, l, s], idx) => {
          const h = ((base + offsets[idx]) % 360 + 360) % 360;
          accents[key] = hslToHex(h, s, l);
        });
      }

      resolve({
        teal:   accents.teal   ?? DEFAULT_THEME_COLORS.teal,
        orange: accents.orange ?? DEFAULT_THEME_COLORS.orange,
        red:    accents.red    ?? DEFAULT_THEME_COLORS.red,
        yellow: accents.yellow ?? DEFAULT_THEME_COLORS.yellow,
        dark:   hslToHex(...dark),
        cream:  hslToHex(...cream),
      });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(DEFAULT_THEME_COLORS); };
    img.src = url;
  });

const AdminApp: React.FC = () => {



  const [orders, setOrders] = useState<PersistedOrderPayload[]>([]);



  const [selectedItems, setSelectedItems] = useState<string[]>([]);



  const [showCompleted, setShowCompleted] = useState(true);



  const [showReady, setShowReady] = useState(true);



  const [statusFilters, setStatusFilters] = useState<StatusFilter[]>(DEFAULT_STATUS_FILTERS);

  // When false (default), test-mode Yoco orders are hidden from the list — matches
  // production behaviour. Toggle on in local dev to see test payments.
  const [showTestOrders, setShowTestOrders] = useState<boolean>(false);

  // Event-date filter for the Orders tab. '' = all events, 'legacy' = orders
  // without an eventDate (pre-feature data), or a YYYY-MM-DD string.
  // Defaults to the next upcoming event date once orders load.
  const [eventDateFilter, setEventDateFilter] = useState<string>('');
  const [eventDateFilterInitialized, setEventDateFilterInitialized] = useState<boolean>(false);



  const [hourFilters, setHourFilters] = useState<HourFilter[]>([]);



  const [countMode, setCountMode] = useState<CountMode>('orders');



  const [graphFilter, setGraphFilter] = useState<GraphFilter>({ hour: null, status: null });



  const [selectedOrder, setSelectedOrder] = useState<PersistedOrderPayload | null>(null);



  const [loading, setLoading] = useState(false);



  const [busy, setBusy] = useState(false);



  const [error, setError] = useState<string | null>(null);



  const [sortField, setSortField] = useState<SortField>('arrival');



  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');



  const [searchTerm, setSearchTerm] = useState<string>('');



  const [canEditStatus, setCanEditStatus] = useState<boolean>(false);



  const [activeTab, setActiveTab] = useState<ActiveTab>('orders');



  const [campaignMonth, setCampaignMonth] = useState<string>(() => getCurrentMonthValue());



  const [dateFrom, setDateFrom] = useState<string>(() => getMonthRange(getCurrentMonthValue()).from);



  const [dateTo, setDateTo] = useState<string>(() => getMonthRange(getCurrentMonthValue()).to);







  const [paymentsDaysBack, setPaymentsDaysBack] = useState<number>(3);



  const [paymentsFrom, setPaymentsFrom] = useState<string>('');



  const [paymentsTo, setPaymentsTo] = useState<string>('');



  const [reconRows, setReconRows] = useState<PaymentReconRow[]>([]);



  const [reconSummary, setReconSummary] = useState<{ orders: number; matched: number; withCsv: number; mismatched: number; csvRows: number } | null>(null);



  const [reconLoading, setReconLoading] = useState(false);



  const [reconError, setReconError] = useState<string | null>(null);



  const [selectedReconRow, setSelectedReconRow] = useState<PaymentReconRow | null>(null);



  const [selectedReconOrder, setSelectedReconOrder] = useState<PersistedOrderPayload | null>(null);

  const [paymentsEnabled, setPaymentsEnabled] = useState<boolean>(true);
  // Independent per-campaign payment status — both load on mount and stay in sync.
  const [paymentsEnabledMain, setPaymentsEnabledMain] = useState<boolean>(true);
  const [paymentsEnabledCatering, setPaymentsEnabledCatering] = useState<boolean>(true);
  const [paymentsToggleSaving, setPaymentsToggleSaving] = useState<{ main: boolean; catering: boolean }>({ main: false, catering: false });

  const [pickupCampaignEnabled, setPickupCampaignEnabled] = useState<boolean>(true);

  const [pickupAddress, setPickupAddress] = useState<string>(
    CAMPAIGN_DETAILS.pickupLocation || DEFAULT_PICKUP_ADDRESS
  );

  const [pickupHours, setPickupHours] = useState<string>(
    CAMPAIGN_DETAILS.pickupWindow || DEFAULT_PICKUP_HOURS
  );

  const [pickupContact, setPickupContact] = useState<string>(
    CAMPAIGN_DETAILS.contactNumber || DEFAULT_PICKUP_CONTACT
  );

  // Per-campaign event date (YYYY-MM-DD). Snapshotted onto each new order so
  // admin can group orders by the event they're for, regardless of when they
  // were created or paid.
  const [eventDate, setEventDate] = useState<string>('');

  const [flyerUrl, setFlyerUrl] = useState<string>('');

  const [flyerUploading, setFlyerUploading] = useState<boolean>(false);
  const [flyerExtracted, setFlyerExtracted] = useState<FlyerExtracted | null>(null);

  const [themeColors, setThemeColors] = useState<ThemeColors>(DEFAULT_THEME_COLORS);

  const [zoneColors, setZoneColors] = useState<ZoneColors>({
    home: { bg: DEFAULT_THEME_COLORS.dark, accent: DEFAULT_THEME_COLORS.teal, text: '#ffffff' },
    menu: { bg: DEFAULT_THEME_COLORS.dark, accent: DEFAULT_THEME_COLORS.orange, text: '#ffffff' },
  });
  const [zoneDirty, setZoneDirty] = useState(false);
  const [extendedColors, setExtendedColors] = useState<string[]>([]);

  const [menuItemsDraft, setMenuItemsDraft] = useState<MenuItem[]>([]);

  const [fontConfig, setFontConfig] = useState<FontConfig>(DEFAULT_FONT_CONFIG);

  const [fontDirty, setFontDirty] = useState(false);
  const [menuDirty, setMenuDirty] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);

  const [settingsSaving, setSettingsSaving] = useState<boolean>(false);

  const [settingsError, setSettingsError] = useState<string | null>(null);

  const [settingsCampaign, setSettingsCampaign] = useState<'onesunday' | 'catering'>('onesunday');

  const [sourceFilter, setSourceFilter] = useState<'all' | 'onesunday' | 'catering'>('all');

  const [adminApiKey, setAdminApiKeyValue] = useState<string>(() => getAdminApiKey());

  const [hasAdminApiKey, setHasAdminApiKey] = useState<boolean>(() => Boolean(getAdminApiKey()));







  const loadOrders = useCallback(async () => {



    setLoading(true);



    try {



      const res = await fetchOrders({



        pageSize: 500,



        sortBy: 'createdAt',



        sortDirection: 'DESC',



        startDate: dateFrom ? toIsoStart(dateFrom) : undefined,



        endDate: dateTo ? toIsoEnd(dateTo) : undefined,



        source: sourceFilter === 'all' ? undefined : sourceFilter,



      });



      setOrders(res.results || []);



      setError(null);



    } catch (err: any) {



      setError(err?.message || 'Failed to load orders');



    } finally {



      setLoading(false);



    }



  }, [dateFrom, dateTo, sourceFilter]);

  const applyAppSettings = useCallback((res: AppSettingsResponse) => {
    if (typeof res.paymentsEnabled === 'boolean') {
      setPaymentsEnabled(res.paymentsEnabled);
    }
    if (typeof res.pickupCampaignEnabled === 'boolean') {
      setPickupCampaignEnabled(res.pickupCampaignEnabled);
    }
    if (typeof res.pickupAddress === 'string') {
      setPickupAddress(res.pickupAddress);
    }
    if (typeof res.pickupHours === 'string') {
      setPickupHours(res.pickupHours);
    }
    if (typeof res.pickupContact === 'string') {
      setPickupContact(res.pickupContact);
    }
    if (typeof res.eventDate === 'string') {
      setEventDate(res.eventDate);
    }
    if (typeof res.flyerUrl === 'string') {
      setFlyerUrl(res.flyerUrl);
    }
    if (res.themeColors && typeof res.themeColors === 'object') {
      setThemeColors(res.themeColors);
      applyThemeColors(res.themeColors);
    }
    if (res.zoneColors?.home && res.zoneColors?.menu) {
      setZoneColors({
        home: { text: '#ffffff', ...res.zoneColors.home },
        menu: { text: '#ffffff', ...res.zoneColors.menu },
      });
    } else if (res.themeColors) {
      setZoneColors({
        home: { bg: res.themeColors.dark, accent: res.themeColors.teal, text: '#ffffff' },
        menu: { bg: res.themeColors.dark, accent: res.themeColors.orange, text: '#ffffff' },
      });
    }
    if (Array.isArray(res.extendedColors) && res.extendedColors.length > 0) {
      setExtendedColors(res.extendedColors);
    }
    if (Array.isArray(res.menuItems)) {
      setMenuItemsDraft(res.menuItems);
    }
    if (res.fontConfig && typeof res.fontConfig === 'object') {
      setFontConfig(res.fontConfig);
      applyFontConfig(res.fontConfig);
    }
    setFontDirty(false);
    setMenuDirty(false);
    setZoneDirty(false);
  }, []);

  const loadAppSettings = useCallback(async (campaign?: 'onesunday' | 'catering') => {
    setSettingsLoading(true);
    try {
      const id = (campaign ?? settingsCampaign) === 'onesunday' ? undefined : (campaign ?? settingsCampaign);
      const res = await fetchAppSettings(id);
      applyAppSettings(res);
      setSettingsError(null);
    } catch (err: any) {
      setSettingsError(err?.message || 'Failed to load app settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [applyAppSettings, settingsCampaign]);

  const normalizeAdminError = useCallback((err: any, fallbackError: string) => {
    const message = err?.message || fallbackError;
    if (typeof message === 'string' && message.toLowerCase().includes('unauthorized')) {
      return 'Admin API key rejected. Update the saved key and try again.';
    }
    return message;
  }, []);

  const ensureAdminApiKey = useCallback(() => {
    const typedKey = adminApiKey.trim();
    if (typedKey) {
      persistAdminApiKey(typedKey);
      setHasAdminApiKey(true);
      return true;
    }

    const existingKey = getAdminApiKey().trim();
    setHasAdminApiKey(Boolean(existingKey));
    return Boolean(existingKey);
  }, [adminApiKey]);

  const saveAppSettings = async (
    payload: UpdateAppSettingsPayload,
    fallbackError: string
  ) => {
    if (canEditStatus && !ensureAdminApiKey()) {
      setSettingsError('Enter the admin API key before changing settings.');
      return;
    }

    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const campaignId = settingsCampaign === 'onesunday' ? undefined : settingsCampaign;
      const updated = await updateAppSettings(payload, campaignId);
      applyAppSettings(updated);
    } catch (err: any) {
      setSettingsError(normalizeAdminError(err, fallbackError));
    } finally {
      setSettingsSaving(false);
    }
  };

  useEffect(() => {

    // check query param for admin editing



    if (typeof window !== 'undefined') {



      const params = new URLSearchParams(window.location.search);



      const isAdmin = params.get('admin') === '1';



      setCanEditStatus(isAdmin);

      if (params.has('insights')) {
        setActiveTab('insights');
      }

    }



  }, []);







  useEffect(() => {



    void loadOrders();



    const id = setInterval(() => {
      void loadOrders();
    }, 30000);



    return () => clearInterval(id);



  }, [loadOrders]);

  useEffect(() => {
    void loadAppSettings();
  }, [loadAppSettings]);







  const loadRecon = async () => {



    setReconLoading(true);



    setReconError(null);



    setSelectedReconRow(null);



    try {



      const res = await fetchPaymentRecon({



        daysBack: paymentsFrom || paymentsTo ? undefined : paymentsDaysBack,



        from: paymentsFrom ? `${paymentsFrom}T00:00:00Z` : undefined,



        to: paymentsTo ? `${paymentsTo}T23:59:59Z` : undefined,



      });



      setReconRows(res.rows || []);



      setReconSummary(res.summary || null);



    } catch (err: any) {



      setReconError(err?.message || 'Failed to run recon');



    } finally {



      setReconLoading(false);



    }



  };







  const handleToggleItem = (itemId: string) => {



    setSelectedItems((prev) =>



      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],



    );



  };







  // All distinct event-date values present on the loaded orders, sorted ascending.
  const eventDateOptions = useMemo(() => {
    const dates = new Set<string>();
    orders.forEach((o) => {
      if (o.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(o.eventDate)) dates.add(o.eventDate);
    });
    return Array.from(dates).sort();
  }, [orders]);

  // Closest upcoming event date (>= today), or null if none in the future.
  const nextUpcomingEventDate = useMemo(() => {
    const todayIso = format(new Date(), 'yyyy-MM-dd');
    return eventDateOptions.find((d) => d >= todayIso) || null;
  }, [eventDateOptions]);

  const hasLegacyOrders = useMemo(
    () => orders.some((o) => !o.eventDate),
    [orders]
  );

  // Default the event selector to the next upcoming event the first time orders
  // arrive. After that, the user is in control.
  useEffect(() => {
    if (eventDateFilterInitialized) return;
    if (orders.length === 0) return;
    setEventDateFilterInitialized(true);
    if (nextUpcomingEventDate) {
      setEventDateFilter(nextUpcomingEventDate);
    }
  }, [orders.length, nextUpcomingEventDate, eventDateFilterInitialized]);

  const campaignScopedOrders = useMemo(() => {
    const fromDate = dateFrom ? startOfDay(parseISO(dateFrom)) : null;
    const toDate = dateTo ? endOfDay(parseISO(dateTo)) : null;
    return orders.filter((order) => {
      if (!showTestOrders && isTestOrder(order)) return false;
      if (!ALLOWED_STATUSES.includes(order.status)) return false;
      if (eventDateFilter === 'legacy') {
        // Show only orders without an event date — fall back to createdAt range.
        if (order.eventDate) return false;
        return inSelectedRange(order, fromDate, toDate);
      }
      if (eventDateFilter) {
        // Showing one event — ignore the calendar range filter; the event date IS the scope.
        return order.eventDate === eventDateFilter;
      }
      // No event filter selected ("All events") — use the calendar range,
      // and include legacy (no-eventDate) orders that fall in range.
      return inSelectedRange(order, fromDate, toDate);
    });
  }, [orders, dateFrom, dateTo, showTestOrders, eventDateFilter]);

  // Same scope as campaignScopedOrders but keeps every status (Pending/Verifying
  // included) so the Insights view can show the full status breakdown and funnel.
  const insightsScopedOrders = useMemo(() => {
    const fromDate = dateFrom ? startOfDay(parseISO(dateFrom)) : null;
    const toDate = dateTo ? endOfDay(parseISO(dateTo)) : null;
    return orders.filter((order) => {
      if (!showTestOrders && isTestOrder(order)) return false;
      if (eventDateFilter === 'legacy') {
        if (order.eventDate) return false;
        return inSelectedRange(order, fromDate, toDate);
      }
      if (eventDateFilter) {
        return order.eventDate === eventDateFilter;
      }
      return inSelectedRange(order, fromDate, toDate);
    });
  }, [orders, dateFrom, dateTo, showTestOrders, eventDateFilter]);

  const availableHours = useMemo(() => {
    const uniq = new Set<string>();
    campaignScopedOrders.forEach((order) => {
      uniq.add(getOrderHourLabel(order));
    });
    return Array.from(uniq).sort((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10));
  }, [campaignScopedOrders]);

  const baseVisibleOrders = useMemo(() => {



    return campaignScopedOrders.filter((order) => {



      if (!showReady && order.status === 'Ready') return false;



      if (!showCompleted && order.status === 'Completed') return false;



      const statusAllowed = statusFilters.includes(order.status as StatusFilter);



      if (!statusAllowed) return false;



      if (selectedItems.length > 0) {



        const hasMatch = (order.items || []).some((item) => selectedItems.includes(item.itemId));



        if (!hasMatch) return false;



      }



      if (hourFilters.length > 0 && !hourFilters.includes(getOrderHourLabel(order))) return false;



      const q = searchTerm.trim().toLowerCase();



      if (q) {



        const matchId = (order.orderId || '').toLowerCase().includes(q);



        const matchCustomer = (order.customer?.name || '').toLowerCase().includes(q);



        const matchItems = (order.items || []).some((i) =>



          (i.name || '').toLowerCase().includes(q),



        );



        if (!matchId && !matchCustomer && !matchItems) return false;



      }



      return true;



    });



  }, [campaignScopedOrders, selectedItems, showCompleted, showReady, statusFilters, hourFilters, searchTerm]);







  const filteredOrders = useMemo(() => {
    return baseVisibleOrders



      .filter((order) => {



        if (graphFilter.hour && getOrderHourLabel(order) !== graphFilter.hour) return false;



      if (graphFilter.status) {



        if (order.status !== graphFilter.status) {



          return false;



        }



      }



      return true;



    })



      .sort((a, b) => {



        let cmp = 0;



        switch (sortField) {



          case 'orderId':



            cmp = (a.orderId || '').localeCompare(b.orderId || '');



            break;



          case 'customer':



            cmp = (a.customer?.name || '').localeCompare(b.customer?.name || '');



            break;



          case 'status':



            cmp = (statusPriority[a.status] || 0) - (statusPriority[b.status] || 0);



            break;



          case 'createdAt':



            cmp =



              new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();



            break;



          case 'arrival':



          default: {



            const ta = getOrderTargetDate(a).getTime();



            const tb = getOrderTargetDate(b).getTime();



            cmp = ta - tb;



            break;



          }



        }







        if (cmp === 0) {



          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();



        }







        return sortDirection === 'asc' ? cmp : -cmp;



      });



  }, [baseVisibleOrders, graphFilter, sortField, sortDirection]);







  const handleStatusUpdate = async (orderId: string, status: OrderStatus) => {



    if (canEditStatus && !ensureAdminApiKey()) {
      setError('Enter the admin API key before updating orders.');
      return;
    }



    setBusy(true);



    try {



      const updated = await updateOrderStatus({ orderId, status });



      setOrders((prev) => prev.map((o) => (o.orderId === orderId ? { ...o, ...updated } : o)));



      setSelectedOrder((prev) =>



        prev && prev.orderId === orderId ? { ...prev, ...updated } : prev,



      );



      setGraphFilter({ hour: null, status: null });



    } catch (err: any) {



      setError(normalizeAdminError(err, 'Failed to update order'));



    } finally {



      setBusy(false);



    }



  };







  const totals = useMemo(() => {
    return campaignScopedOrders.reduce(



      (acc, order) => {



        if (isTestOrder(order) || !ALLOWED_STATUSES.includes(order.status)) return acc;



        acc.total += order.totalAmount || 0;



        acc.counts[order.status] = (acc.counts[order.status] || 0) + 1;



        if (order.status === 'Late') acc.late += 1;



        return acc;



      },



      {



        total: 0,



        late: 0,



        counts: { Pending: 0, Verifying: 0, Paid: 0, Ready: 0, Completed: 0, Late: 0 } as Record<



          OrderStatus,



          number



        >,



      },



    );



  }, [campaignScopedOrders]);







  const handleSortChange = (field: SortField) => {



    setSortDirection((prev) => {



      if (field !== sortField) {



        return field === 'createdAt' ? 'desc' : 'asc';



      }



      return prev === 'asc' ? 'desc' : 'asc';



    });



    setSortField(field);



  };







  const handleToggleStatusFilter = (status: StatusFilter) => {



    setStatusFilters((prev) =>



      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],



    );



  };







  const handleResetStatusFilters = () => {



    setStatusFilters(DEFAULT_STATUS_FILTERS);



  };







  const handleToggleHourFilter = (hour: HourFilter) => {



    setHourFilters((prev) =>



      prev.includes(hour) ? prev.filter((h) => h !== hour) : [...prev, hour],



    );



  };







  const handleResetHourFilters = () => {



    setHourFilters([]);



  };

  const campaignMonthLabel = useMemo(() => {
    const parsed = parseISO(`${campaignMonth}-01`);
    if (Number.isNaN(parsed.getTime())) return campaignMonth;
    return format(parsed, 'MMMM yyyy');
  }, [campaignMonth]);

  const applyCampaignMonth = (monthValue: string) => {
    if (!monthValue) return;
    setCampaignMonth(monthValue);
    const monthRange = getMonthRange(monthValue);
    setDateFrom(monthRange.from);
    setDateTo(monthRange.to);
    setGraphFilter({ hour: null, status: null });
    setHourFilters([]);
  };







  const handleResetAll = () => {



    setSelectedItems([]);



    setShowCompleted(true);



    setShowReady(true);



    setStatusFilters(DEFAULT_STATUS_FILTERS);



    setHourFilters([]);



    setCountMode('orders');



    setGraphFilter({ hour: null, status: null });



    setSortField('arrival');



    setSortDirection('asc');



    setSelectedOrder(null);



    setSearchTerm('');



    const currentMonth = getCurrentMonthValue();
    const monthRange = getMonthRange(currentMonth);
    setCampaignMonth(currentMonth);
    setDateFrom(monthRange.from);
    setDateTo(monthRange.to);

    void loadOrders();
    void loadAppSettings();



  };



  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const emailScope: EmailScope = useMemo(() => {
    const sourceParam = sourceFilter === 'all' ? undefined : sourceFilter;
    const usingEventDate = !!eventDateFilter && eventDateFilter !== 'legacy';
    if (usingEventDate) {
      return {
        source: sourceParam,
        eventDate: eventDateFilter,
        description: `event ${eventDateFilter}${sourceParam ? ` · ${sourceParam}` : ''}`,
      };
    }
    return {
      source: sourceParam,
      createdFrom: dateFrom ? toIsoStart(dateFrom) : undefined,
      createdTo: dateTo ? toIsoEnd(dateTo) : undefined,
      description: `orders created ${dateFrom || '…'} → ${dateTo || '…'}${sourceParam ? ` · ${sourceParam}` : ''}`,
    };
  }, [sourceFilter, eventDateFilter, dateFrom, dateTo]);
  const canOpenEmailModal = !!emailScope.eventDate || (!!emailScope.createdFrom && !!emailScope.createdTo);
  const handleOpenEmailModal = () => {
    if (!canOpenEmailModal) {
      window.alert('Pick an event in the filter, or set a date range, before composing an email.');
      return;
    }
    setEmailModalOpen(true);
  };

  // Kitchen-friendly CSV of the currently-filtered orders. Strips analytics
  // fields (sessionId, UTM, geo, userAgent etc) and keeps only the columns a
  // pass-line operator needs to fulfil orders. Pre-sorted by arrival time
  // then order id so the file reads as a service timeline.
  const handleDownloadCsv = () => {
    const source = campaignScopedOrders;
    if (source.length === 0) {
      window.alert('No orders match the current filter — nothing to export.');
      return;
    }
    const arrivalOf = (o: PersistedOrderPayload) =>
      (o.customer as { arrivalTime?: string } | undefined)?.arrivalTime || '';
    const sorted = [...source].sort((a, b) => {
      const at = arrivalOf(a);
      const bt = arrivalOf(b);
      if (at !== bt) return at.localeCompare(bt);
      return (a.orderId || '').localeCompare(b.orderId || '');
    });
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const headers = [
      'orderId', 'status', 'orderType', 'arrivalTime',
      'customerName',
      'itemName', 'quantity', 'unitPrice', 'lineTotal',
      'orderTotal', 'notes', 'eventDate',
    ];
    const lines: string[] = ['sep=,', headers.map(escape).join(';')];
    for (const o of sorted) {
      const items = o.items || [];
      const baseFields = [
        o.orderId, o.status, o.orderType, arrivalOf(o),
        o.customer?.name || '',
      ];
      const trailFields = [
        o.totalAmount,
        o.customer?.notes || '',
        o.eventDate || '',
      ];
      if (items.length === 0) {
        lines.push([...baseFields, '', '', '', '', ...trailFields].map(escape).join(';'));
      } else {
        for (const it of items) {
          lines.push(
            [
              ...baseFields,
              it.name, it.quantity, it.unitPrice, it.lineTotal,
              ...trailFields,
            ].map(escape).join(';'),
          );
        }
      }
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scopeTag = eventDateFilter && eventDateFilter !== 'legacy'
      ? `event-${eventDateFilter}`
      : `${dateFrom || 'start'}_${dateTo || 'end'}`;
    a.download = `onesunday-orders-${scopeTag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePaymentsToggle = async () => {
    const nextValue = !paymentsEnabled;
    await saveAppSettings(
      { paymentsEnabled: nextValue },
      'Failed to update payment settings'
    );
  };

  // Load both campaigns' payment-enabled state once on mount so the two-toggle UI
  // can show them side-by-side regardless of which campaign is currently selected.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [main, catering] = await Promise.all([
          fetchAppSettings(undefined),
          fetchAppSettings('catering'),
        ]);
        if (cancelled) return;
        setPaymentsEnabledMain(main.paymentsEnabled);
        setPaymentsEnabledCatering(catering.paymentsEnabled);
      } catch (_err) {
        // Settings load failure is non-fatal — toggles fall back to defaults.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePaymentsFor = async (target: 'main' | 'catering') => {
    if (canEditStatus && !ensureAdminApiKey()) {
      setSettingsError('Enter the admin API key before changing settings.');
      return;
    }
    const campaignId = target === 'catering' ? 'catering' : undefined;
    const current = target === 'main' ? paymentsEnabledMain : paymentsEnabledCatering;
    const next = !current;
    setPaymentsToggleSaving((s) => ({ ...s, [target]: true }));
    setSettingsError(null);
    try {
      await updateAppSettings({ paymentsEnabled: next }, campaignId);
      if (target === 'main') {
        setPaymentsEnabledMain(next);
        if (settingsCampaign === 'onesunday') setPaymentsEnabled(next);
      } else {
        setPaymentsEnabledCatering(next);
        if (settingsCampaign === 'catering') setPaymentsEnabled(next);
      }
    } catch (err: any) {
      setSettingsError(normalizeAdminError(err, 'Failed to update payment settings'));
    } finally {
      setPaymentsToggleSaving((s) => ({ ...s, [target]: false }));
    }
  };

  const handlePickupCampaignToggle = async () => {
    const nextValue = !pickupCampaignEnabled;
    await saveAppSettings(
      { pickupCampaignEnabled: nextValue },
      'Failed to update campaign type'
    );
  };

  const handlePickupDetailsSave = async () => {
    await saveAppSettings(
      {
        pickupAddress: pickupAddress.trim(),
        pickupHours: pickupHours.trim(),
        pickupContact: pickupContact.trim(),
        eventDate: eventDate.trim(),
      },
      'Failed to update pickup settings'
    );
  };

  const handleZoneColorsSave = async () => {
    await saveAppSettings({ zoneColors, extendedColors }, 'Failed to save zone colours');
    setZoneDirty(false);
  };

  const handleMenuItemsSave = async () => {
    await saveAppSettings({ menuItems: menuItemsDraft }, 'Failed to save menu items');
    setMenuDirty(false);
  };

  const handleFontConfigSave = async () => {
    applyFontConfig(fontConfig);
    await saveAppSettings({ fontConfig }, 'Failed to save typography');
    setFontDirty(false);
  };

  const handleFlyerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ensureAdminApiKey()) {
      setSettingsError('Enter the admin API key before uploading.');
      return;
    }
    setFlyerUploading(true);
    setSettingsError(null);
    setFlyerExtracted(null);
    try {
      // Upload, OCR extraction, palette extraction, and font mood detection all in parallel
      const [url, ocrExtracted, suggestedColors, allPixels] = await Promise.all([
        fileToBase64(file).then((b64) => uploadFlyer(b64, file.type)),
        extractFlyerInfoOcr(file),
        extractPaletteFromFile(file),
        // Re-sample pixels for font mood detection (lightweight 60×60 pass)
        new Promise<[number, number, number][]>((res) => {
          const u = URL.createObjectURL(file);
          const im = new Image();
          im.onload = () => {
            URL.revokeObjectURL(u);
            const cv = document.createElement('canvas'); cv.width = 60; cv.height = 60;
            const cx = cv.getContext('2d');
            if (!cx) { res([]); return; }
            cx.drawImage(im, 0, 0, 60, 60);
            const { data } = cx.getImageData(0, 0, 60, 60);
            const px: [number, number, number][] = [];
            for (let i = 0; i < data.length; i += 4) {
              if (data[i + 3] < 128) continue;
              px.push(rgbToHsl(data[i], data[i + 1], data[i + 2]));
            }
            res(px);
          };
          im.onerror = () => { URL.revokeObjectURL(u); res([]); };
          im.src = u;
        }),
      ]);
      const suggestedFonts = suggestFontsFromPixels(allPixels);
      const dominantExtracted = extractDominantColors(allPixels, 10);
      setFlyerUrl(url);
      setThemeColors(suggestedColors);
      applyThemeColors(suggestedColors);
      setFontConfig(suggestedFonts);
      applyFontConfig(suggestedFonts);
      setExtendedColors(dominantExtracted);
      // Reset zone colours — prefer actual dominant flyer colours over hue-bucketed slots
      const homeAccent = dominantExtracted[0] ?? suggestedColors.teal;
      const menuAccent = dominantExtracted[1] ?? dominantExtracted[0] ?? suggestedColors.orange;
      setZoneColors({
        home: { bg: suggestedColors.dark, accent: homeAccent, text: '#ffffff' },
        menu: { bg: suggestedColors.dark, accent: menuAccent, text: '#ffffff' },
      });
      const hasAny = Object.values(ocrExtracted).some(Boolean);
      if (hasAny) setFlyerExtracted(ocrExtracted);
      await saveAppSettings(
        { flyerUrl: url, themeColors: suggestedColors, fontConfig: suggestedFonts, extendedColors: dominantExtracted },
        'Failed to save flyer',
      );
    } catch (err: any) {
      setSettingsError(err?.message || 'Upload failed');
    } finally {
      setFlyerUploading(false);
      e.target.value = '';
    }
  };

  const handleAdminApiKeySave = () => {
    const normalized = adminApiKey.trim();
    if (!normalized) {
      setSettingsError('Enter the admin API key before saving it locally.');
      setHasAdminApiKey(Boolean(getAdminApiKey()));
      return;
    }

    persistAdminApiKey(normalized);
    setAdminApiKeyValue(normalized);
    setHasAdminApiKey(true);
    setSettingsError(null);
  };

  const handleAdminApiKeyClear = () => {
    clearAdminApiKey();
    setAdminApiKeyValue('');
    setHasAdminApiKey(false);
    setSettingsError(null);
  };

  return (



    <div className="min-h-screen flex flex-col font-sans text-admin-text bg-admin-surface-2">

      <div className="sticky top-0 z-20 border-b border-admin-border bg-admin-surface">

        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">

          {/* Title row — full width, no flex squeeze */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-admin-text tracking-tight">
                {CAMPAIGN_DETAILS.controlTitle}
              </h1>
              <p className="mt-0.5 text-sm text-admin-muted">
                {CAMPAIGN_DETAILS.campaignName}
                {!paymentsEnabled && ' · Payments disabled'}
              </p>
            </div>
            {error && (
              <div className="inline-flex items-center gap-1.5 text-sm text-[#A41C2C]">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <nav className="grid grid-cols-2 gap-2 border-t border-admin-border/60 pt-3 sm:grid-cols-4" aria-label="Admin sections">
            {([
              { tab: 'orders' as const, label: 'Orders', description: 'View and fulfil customer orders' },
              { tab: 'payments' as const, label: 'Payments', description: 'Reconcile Yoco transactions' },
              { tab: 'insights' as const, label: 'Insights', description: 'Track sales and popular items' },
              { tab: 'settings' as const, label: 'Campaign settings', description: 'Manage menu and checkout' },
            ]).map(({ tab, label, description }) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-admin-accent/30 ${
                    isActive
                      ? 'border-admin-accent bg-admin-accent-soft text-admin-text'
                      : 'border-admin-border bg-admin-surface text-admin-muted hover:border-admin-text/30 hover:text-admin-text'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className="mt-0.5 block text-xs leading-4 text-admin-text/70">{description}</span>
                </button>
              );
            })}
          </nav>



          {/* Toolbar — Orders + Insights. Wraps cleanly on narrow screens. */}
          {(activeTab === 'orders' || activeTab === 'insights') && (
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 min-w-[16rem] max-w-md">
                <input
                  type="text"
                  placeholder="Search orders, customers, items…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-md border border-admin-border bg-admin-surface pl-3 pr-8 py-2 text-sm text-admin-text placeholder:text-admin-subtle focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-admin-subtle hover:text-admin-text text-base leading-none"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Date range */}
              <div className="inline-flex items-stretch rounded-md border border-admin-border bg-admin-surface text-sm overflow-hidden">
                <input
                  type="month"
                  aria-label="Month"
                  value={campaignMonth}
                  onChange={(e) => applyCampaignMonth(e.target.value)}
                  className="px-2 py-2 bg-transparent text-admin-text focus:outline-none focus:bg-admin-surface-2"
                />
                <span className="self-center text-admin-subtle text-xs">›</span>
                <input
                  type="date"
                  aria-label="From"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    if (e.target.value) setCampaignMonth(e.target.value.slice(0, 7));
                  }}
                  className="px-2 py-2 bg-transparent text-admin-text focus:outline-none focus:bg-admin-surface-2"
                />
                <span className="self-center text-admin-subtle text-xs px-1">–</span>
                <input
                  type="date"
                  aria-label="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-2 bg-transparent text-admin-text focus:outline-none focus:bg-admin-surface-2"
                />
                <button
                  type="button"
                  onClick={() => applyCampaignMonth(getCurrentMonthValue())}
                  className="px-3 text-xs font-medium text-admin-muted hover:text-admin-text border-l border-admin-border hover:bg-admin-surface-2"
                >
                  This month
                </button>
              </div>

              {/* App (source) — segmented control */}
              <div className="inline-flex items-stretch rounded-md border border-admin-border bg-admin-surface text-sm overflow-hidden">
                {([
                  { key: 'all' as const, label: 'All apps' },
                  { key: 'onesunday' as const, label: 'One Sunday' },
                  { key: 'catering' as const, label: 'Catering' },
                ]).map(({ key, label }) => {
                  const active = sourceFilter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSourceFilter(key)}
                      className={`px-3 py-2 text-sm transition-colors border-l border-admin-border first:border-l-0 ${
                        active
                          ? 'bg-admin-accent-soft text-admin-accent font-medium'
                          : 'text-admin-muted hover:text-admin-text'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Event date */}
              <label className="inline-flex items-center gap-2 rounded-md border border-admin-border bg-admin-surface pl-3 pr-1 py-1 text-sm">
                <span className="text-admin-muted">Event</span>
                <select
                  value={eventDateFilter}
                  onChange={(e) => setEventDateFilter(e.target.value)}
                  className="bg-transparent text-admin-text font-medium pr-1 py-1 focus:outline-none cursor-pointer"
                >
                  <option value="">All</option>
                  {eventDateOptions.length > 0 && (
                    <optgroup label="Events">
                      {eventDateOptions.map((d) => (
                        <option key={d} value={d}>
                          {format(parseISO(d), 'd MMM yyyy')}
                          {nextUpcomingEventDate === d ? '  · upcoming' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {hasLegacyOrders && (
                    <option value="legacy">Legacy (no event date)</option>
                  )}
                </select>
              </label>

              {/* Test toggle */}
              <button
                type="button"
                onClick={() => setShowTestOrders((v) => !v)}
                title="Show Yoco test-mode orders alongside live ones."
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  showTestOrders
                    ? 'border-admin-accent bg-admin-accent-soft text-admin-accent font-medium'
                    : 'border-admin-border bg-admin-surface text-admin-muted hover:text-admin-text'
                }`}
              >
                Test orders {showTestOrders ? 'on' : 'off'}
              </button>

              {/* Email customers (admin only). Opens a composer scoped to the
                  picked event, or to the current calendar range when no event
                  is selected. Always two-stage (dry-run preview → confirm). */}
              {canEditStatus && (
                <button
                  type="button"
                  onClick={handleOpenEmailModal}
                  title="Compose and send an email to paid customers in the active filter. Variables like {{name}} are replaced per recipient. Always shows a preview before committing."
                  className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 transition-colors"
                >
                  Email customers
                </button>
              )}

              {/* Download CSV — kitchen-friendly export of the currently
                  filtered orders. Strips analytics + contact PII; keeps only
                  the columns a pass-line operator needs to fulfil orders. */}
              {canEditStatus && (
                <button
                  type="button"
                  onClick={handleDownloadCsv}
                  title="Download the currently-filtered orders as a kitchen-sheet CSV (one row per line item; analytics + contact details stripped, customer name kept)."
                  className="inline-flex items-center gap-2 rounded-md border border-admin-border bg-admin-surface px-3 py-2 text-sm font-medium text-admin-muted hover:text-admin-text transition-colors"
                >
                  Download CSV
                </button>
              )}

              {/* Spacer pushes the actions to the right when there's space */}
              <div className="flex-1 min-w-0" />

              {/* Actions */}
              <button
                type="button"
                onClick={handleResetAll}
                className="inline-flex items-center gap-2 rounded-md border border-admin-border bg-admin-surface px-3 py-2 text-sm font-medium text-admin-muted hover:text-admin-text transition-colors"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => void loadOrders()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-md bg-admin-accent hover:bg-admin-accent-hover px-3 py-2 text-sm font-medium text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-admin-accent/30 focus:ring-offset-2"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          )}







          {activeTab === 'orders' && (
            <>

            <FilterBar



              selectedItems={selectedItems}



              onToggleItem={handleToggleItem}



              showCompleted={showCompleted}



            onToggleShowCompleted={() => setShowCompleted((v) => !v)}



            showReady={showReady}



            onToggleShowReady={() => setShowReady((v) => !v)}



            statusFilters={statusFilters}



            onToggleStatusFilter={handleToggleStatusFilter}



            onResetStatusFilters={handleResetStatusFilters}



            hourFilters={hourFilters}



            hourOptions={availableHours}



            onToggleHourFilter={handleToggleHourFilter}



            onResetHourFilters={handleResetHourFilters}



            countMode={countMode}



            onToggleCountMode={setCountMode}



            onClearItems={() => setSelectedItems([])}



            className="flex flex-wrap items-center gap-3"



            statusCounts={totals.counts}



              late={totals.late}



              revenueText={currency.format(totals.total)}



            />

            </>
          )}



        </div>






      </div>







      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6 space-y-6">



        {activeTab === 'orders' ? (



          <>



            














            <div className="mb-3 flex items-center justify-between">



              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">



                Orders



                <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">



                  {filteredOrders.length}



                </span>



              </h2>



            </div>







            <OrderList



              orders={filteredOrders}



              onOrderClick={setSelectedOrder}



              selectedItems={selectedItems}



              sortField={sortField}



              sortDirection={sortDirection}



              onSortChange={handleSortChange}



            />



          </>



        ) : activeTab === 'payments' ? (



          <div className="space-y-4">



            <div className="flex flex-wrap items-center gap-3">



              <label className="text-sm text-gray-700 flex items-center gap-2">



                From



                <input



                  type="date"



                  value={paymentsFrom}



                  onChange={(e) => {



                    setPaymentsFrom(e.target.value);



                  }}



                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-500"



                />



              </label>



              <label className="text-sm text-gray-700 flex items-center gap-2">



                To



                <input



                  type="date"



                  value={paymentsTo}



                  onChange={(e) => {



                    setPaymentsTo(e.target.value);



                  }}



                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-500"



                />



              </label>



              <label className="text-sm text-gray-700 flex items-center gap-2">



                Days back



                <input



                  type="number"



                  min={1}



                  max={30}



                  value={paymentsDaysBack}



                  onChange={(e) => {



                    const next = Number(e.target.value) || 1;



                    setPaymentsDaysBack(Math.min(30, Math.max(1, next)));



                  }}



                  className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-500"



                  disabled={Boolean(paymentsFrom || paymentsTo)}



                  title={paymentsFrom || paymentsTo ? 'Disabled when using From/To' : ''}



                />



              </label>



              <div className="flex items-center gap-2">



                <button



                  onClick={loadRecon}



                  disabled={reconLoading}



                  className="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"



                >



                  {reconLoading ? 'Reconning…' : 'Run recon'}



                </button>



                {reconSummary && (



                  <span className="text-xs text-gray-700">



                    Orders: {reconSummary.orders} · Matched: {reconSummary.matched} · With CSV: {reconSummary.withCsv} · Mismatched: {reconSummary.mismatched}



                  </span>



                )}



              </div>



              {reconError && <span className="text-sm text-red-600">{reconError}</span>}



            </div>







            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-2">



              <h3 className="text-sm font-semibold text-gray-800">Recon results</h3>



              {reconLoading && <div className="text-sm text-gray-500">Running recon...</div>}



              {!reconLoading && reconRows.length === 0 && (



                <div className="text-sm text-gray-500">Run recon to see matches.</div>



              )}



              {!reconLoading && reconRows.length > 0 && (



                <div className="space-y-3">



                  <div className="max-h-[520px] overflow-auto border border-gray-100 rounded-lg">



                    <table className="min-w-full text-xs divide-y divide-gray-200">



                      <thead className="bg-gray-50 text-gray-600">



                        <tr>



                          <th className="px-3 py-2 text-left">Order</th>



                          <th className="px-3 py-2 text-left">Order Status</th>



                          <th className="px-3 py-2 text-left">Checkout</th>



                          <th className="px-3 py-2 text-left">Payment ID</th>



                          <th className="px-3 py-2 text-left">Yoco Status</th>



                          <th className="px-3 py-2 text-left">Customer</th>



                          <th className="px-3 py-2 text-left">Items</th>



                          <th className="px-3 py-2 text-left">Order Amt</th>



                          <th className="px-3 py-2 text-left">Yoco Amt</th>



                          <th className="px-3 py-2 text-left">Diff</th>



                          <th className="px-3 py-2 text-left">Recon</th>



                        </tr>



                      </thead>



                      <tbody className="divide-y divide-gray-100">



                        {reconRows.map((r) => (



                          <tr



                            key={`${r.orderId || 'nopk'}-${r.checkoutId || 'nocheckout'}-${r.paymentId || 'nopay'}`}



                            className={`${r.diffCents ? 'bg-amber-50' : ''} ${



                              selectedReconRow?.orderId === r.orderId && selectedReconRow?.paymentId === r.paymentId



                                ? 'ring-1 ring-blue-300'



                                : ''



                            } hover:bg-gray-50 cursor-pointer`}



                            onClick={() => {



                              setSelectedReconRow(r);



                              const foundOrder = orders.find((o) => (o.orderId || o.id) === r.orderId);



                              setSelectedReconOrder(foundOrder || null);



                            }}



                          >



                            <td className="px-3 py-2 font-mono text-gray-900">{r.orderId || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">{r.status || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">{r.checkoutId || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">{r.paymentId || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">{r.yocoStatus || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">{r.customerName || '-'}</td>



                            <td className="px-3 py-2 text-gray-700">



                              {Array.isArray(r.items) && r.items.length



                                ? r.items



                                    .map((it) => `${it.quantity ?? 0}x ${it.name ?? ''}`.trim())



                                    .join(', ')



                                : '-'}



                            </td>



                            <td className="px-3 py-2 text-gray-800">



                              {typeof r.orderAmountCents === 'number'



                                ? `R ${(r.orderAmountCents / 100).toFixed(2)}`



                                : '-'}



                            </td>



                            <td className="px-3 py-2 text-gray-800">



                              {typeof r.yocoAmountCents === 'number'



                                ? `R ${(r.yocoAmountCents / 100).toFixed(2)}`



                                : '-'}



                            </td>



                            <td className="px-3 py-2 text-gray-800">



                              {typeof r.diffCents === 'number' ? `R ${(r.diffCents / 100).toFixed(2)}` : '-'}



                            </td>



                            <td className="px-3 py-2 text-gray-700">



                              {(() => {



                                const yStat = (r.yocoStatus || '').toLowerCase();



                                if (yStat.includes('fail')) return '❌';



                                if (typeof r.diffCents === 'number') {



                                  return r.diffCents === 0 ? '✅ OK' : '⚠️ AMOUNT';



                                }



                                return r.yocoAmountCents ? '✅ OK' : '—';



                              })()}



                            </td>



                          </tr>



                        ))}



                      </tbody>



                    </table>



                  </div>



                  {selectedReconRow && (



                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">



                      <div className="border border-gray-100 rounded-lg bg-gray-50 p-3">



                        <div className="text-sm font-semibold text-gray-800 mb-1">Selected row (recon)</div>



                        <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-auto">



                          {JSON.stringify(selectedReconRow, null, 2)}



                        </pre>



                      </div>



                      <div className="border border-gray-100 rounded-lg bg-gray-50 p-3">



                        <div className="text-sm font-semibold text-gray-800 mb-1">Order detail</div>



                        {selectedReconOrder ? (



                          <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-auto">



                            {JSON.stringify(



                              {



                                orderId: selectedReconOrder.orderId || selectedReconOrder.id,



                                status: selectedReconOrder.status,



                                checkoutId: selectedReconRow?.checkoutId,



                                customer: { name: selectedReconOrder.customer?.name },



                                items: selectedReconOrder.items,



                                totalAmount: selectedReconOrder.totalAmount,



                                paymentInfo:



                                  canEditStatus || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('admin') === '1'



                                    ? selectedReconOrder.paymentInfo



                                    : undefined,



                              },



                              null,



                              2,



                            )}



                          </pre>



                        ) : (



                          <div className="text-xs text-gray-600">No matching order loaded.</div>



                        )}



                      </div>



                    </div>



                  )}



                </div>



              )}



            </div>



          </div>



        ) : activeTab === 'insights' ? (

          <InsightsTab
            orders={insightsScopedOrders}
            dateFrom={dateFrom}
            dateTo={dateTo}
            showTestOrders={showTestOrders}
          />

        ) : (



          <div className="space-y-4">
            <div className="rounded-xl border border-admin-text/12 bg-admin-surface p-4 shadow-sm">
              <h2 className="text-lg font-semibold text-admin-text tracking-tight">Settings</h2>
              <p className="text-sm text-admin-text/70 mt-1">
                Configure campaign behavior and checkout controls for the selected event.
              </p>
              <div className="mt-3">
                <p className="text-xs font-semibold text-admin-text mb-1.5">Editing settings for</p>
                <div className="flex gap-2">
                  {(['onesunday', 'catering'] as const).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setSettingsCampaign(c);
                        setFlyerExtracted(null);
                        void loadAppSettings(c);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                        settingsCampaign === c
                          ? 'bg-admin-text text-white border-admin-text'
                          : 'bg-white text-admin-text border-admin-border hover:bg-admin-accent-soft'
                      }`}
                    >
                      {c === 'onesunday' ? 'One Sunday (May 3)' : 'Catering (May 2)'}
                    </button>
                  ))}
                </div>
              </div>
              {!canEditStatus && (
                <p className="text-xs text-amber-700 mt-2">
                  Read-only mode. Open admin with <code>?admin=1</code> to edit settings.
                </p>
              )}
              {settingsError && (
                <p className="text-xs text-red-600 mt-2">{settingsError}</p>
              )}
            </div>

            {canEditStatus && (
              <div className="bg-admin-surface border border-admin-text/12 rounded-xl shadow-sm p-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-admin-text">Admin API Key</h3>
                  <p className="text-xs text-admin-text/70 mt-1">
                    Required for protected settings changes. Stored in this browser only.
                  </p>
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-end">
                  <label className="flex-1 text-xs text-admin-text/70">
                    API key
                    <input
                      type="password"
                      autoComplete="off"
                      value={adminApiKey}
                      onChange={(e) => setAdminApiKeyValue(e.target.value)}
                      className="mt-1 w-full border border-admin-text/18 rounded-lg px-2 py-1.5 text-sm text-admin-text focus:outline-none focus:border-admin-accent"
                      placeholder={hasAdminApiKey ? 'Saved locally' : 'Enter ADMIN_API_KEY'}
                    />
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAdminApiKeySave}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg border border-admin-border bg-white text-xs font-semibold text-admin-text hover:bg-admin-accent-soft"
                    >
                      Save key
                    </button>
                    <button
                      type="button"
                      onClick={handleAdminApiKeyClear}
                      disabled={!hasAdminApiKey && !adminApiKey}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg border border-admin-border bg-white text-xs font-semibold text-admin-text hover:bg-admin-accent-soft disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Clear key
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-admin-surface border border-admin-border rounded-xl shadow-sm p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-admin-text">Payments</h3>
                <p className="text-xs text-admin-muted mt-0.5">
                  Switch checkout on or off for each app independently. When off, customers can still browse the menu but the checkout button is hidden.
                </p>
              </div>
              <div className="divide-y divide-admin-border">
                {([
                  { key: 'main' as const, label: 'Main app (One Sunday)', enabled: paymentsEnabledMain, saving: paymentsToggleSaving.main },
                  { key: 'catering' as const, label: 'Catering', enabled: paymentsEnabledCatering, saving: paymentsToggleSaving.catering },
                ]).map(({ key, label, enabled, saving }) => (
                  <div key={key} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-admin-text">{label}</p>
                      <p className="text-xs text-admin-muted">
                        {enabled ? 'Customers can pay.' : 'Checkout disabled — menu still visible.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void togglePaymentsFor(key)}
                      disabled={!canEditStatus || saving}
                      role="switch"
                      aria-checked={enabled}
                      aria-label={`${enabled ? 'Disable' : 'Enable'} payments for ${label}`}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-admin-accent/40 focus:ring-offset-2 focus:ring-offset-admin-surface ${
                        enabled ? 'bg-admin-success' : 'bg-admin-border'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          enabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                      {saving && (
                        <span className="absolute -right-6 top-1/2 -translate-y-1/2 text-[10px] font-medium text-admin-muted">…</span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-admin-surface border border-admin-text/12 rounded-xl shadow-sm p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-admin-text">Campaign Type</h3>
                  <p className="text-xs text-admin-text/70">Set whether this month runs as a pickup campaign.</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                      pickupCampaignEnabled
                        ? 'bg-admin-accent-soft text-admin-text border-admin-accent/40'
                        : 'bg-stone-100 text-stone-700 border-stone-300'
                    }`}
                  >
                    {pickupCampaignEnabled ? 'Pickup' : 'Non-pickup'}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handlePickupCampaignToggle()}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border border-admin-border bg-white text-admin-text hover:bg-admin-accent-soft disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {settingsSaving
                      ? 'Saving...'
                      : pickupCampaignEnabled
                        ? 'Switch to Non-pickup'
                        : 'Switch to Pickup'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="text-xs text-admin-text/70">
                  Pickup address
                  <input
                    type="text"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="mt-1 w-full border border-admin-text/18 rounded-lg px-2 py-1.5 text-sm text-admin-text focus:outline-none focus:border-admin-accent disabled:opacity-60"
                    placeholder={DEFAULT_PICKUP_ADDRESS}
                  />
                </label>
                <label className="text-xs text-admin-text/70">
                  Pickup hours
                  <input
                    type="text"
                    value={pickupHours}
                    onChange={(e) => setPickupHours(e.target.value)}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="mt-1 w-full border border-admin-text/18 rounded-lg px-2 py-1.5 text-sm text-admin-text focus:outline-none focus:border-admin-accent disabled:opacity-60"
                    placeholder={DEFAULT_PICKUP_HOURS}
                  />
                </label>
                <label className="text-xs text-admin-text/70">
                  Pickup contact
                  <input
                    type="text"
                    value={pickupContact}
                    onChange={(e) => setPickupContact(e.target.value)}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="mt-1 w-full border border-admin-text/18 rounded-lg px-2 py-1.5 text-sm text-admin-text focus:outline-none focus:border-admin-accent disabled:opacity-60"
                    placeholder={DEFAULT_PICKUP_CONTACT}
                  />
                </label>
              </div>

              <div>
                <label className="text-xs text-admin-text/70 block">
                  <span className="flex items-baseline justify-between gap-2">
                    <span>Next event date</span>
                    <span className="text-[10px] text-admin-subtle font-normal">
                      Stamped onto every new order so the admin can group orders by event.
                    </span>
                  </span>
                  <input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="mt-1 w-full border border-admin-text/18 rounded-lg px-2 py-1.5 text-sm text-admin-text focus:outline-none focus:border-admin-accent disabled:opacity-60"
                  />
                </label>
                {eventDate && (
                  <button
                    type="button"
                    onClick={() => setEventDate('')}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    className="mt-1 text-[11px] text-admin-muted hover:text-admin-accent disabled:opacity-60"
                  >
                    Clear event date
                  </button>
                )}
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => void handlePickupDetailsSave()}
                  disabled={!canEditStatus || settingsLoading || settingsSaving}
                  className="inline-flex items-center px-3 py-1.5 rounded-lg border border-admin-border bg-white text-xs font-semibold text-admin-text hover:bg-admin-accent-soft disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {settingsSaving ? 'Saving...' : 'Save Pickup Settings'}
                </button>
              </div>
            </div>

            {/* ── Campaign Flyer ── */}
          <div className="rounded-xl border border-admin-border bg-white/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-admin-text">Campaign Flyer</h3>
                <p className="text-xs text-admin-subtle">Click the image to replace. Auto-detects zone colours, font &amp; address.</p>
              </div>
            </div>
            <label className={`relative block w-full aspect-[3/2] rounded-lg overflow-hidden border border-admin-border bg-admin-surface-2/20 group ${canEditStatus && !flyerUploading ? 'cursor-pointer' : 'cursor-default'}`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => void handleFlyerUpload(e)}
                disabled={!canEditStatus || flyerUploading || settingsLoading}
                className="sr-only"
              />
              {flyerUrl ? (
                <img
                  src={flyerUrl}
                  alt="Current flyer"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-admin-subtle">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <span className="text-xs">No flyer — click to upload</span>
                </div>
              )}
              {/* Hover overlay */}
              {canEditStatus && !flyerUploading && (
                <div className="absolute inset-0 bg-admin-surface-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-xs font-semibold tracking-wide">Replace flyer</span>
                </div>
              )}
              {/* Upload spinner overlay */}
              {flyerUploading && (
                <div className="absolute inset-0 bg-admin-text/60 flex items-center justify-center">
                  <span className="text-white text-xs font-semibold animate-pulse">Uploading &amp; scanning…</span>
                </div>
              )}
            </label>
          {/* ── Extracted flyer info ── */}
          {flyerExtracted && (() => {
            const fields: { label: string; value: string | null; onUse: () => void }[] = [
              {
                label: 'Address',
                value: flyerExtracted.address,
                onUse: () => setPickupAddress(flyerExtracted.address!),
              },
              {
                label: 'Venue',
                value: flyerExtracted.venueName,
                onUse: () => setPickupAddress(flyerExtracted.venueName!),
              },
              {
                label: 'Hours',
                value: flyerExtracted.eventHours,
                onUse: () => setPickupHours(flyerExtracted.eventHours!),
              },
              {
                label: 'Date',
                value: flyerExtracted.eventDate,
                onUse: () => {},
              },
              {
                label: 'Contact',
                value: flyerExtracted.contactNumber,
                onUse: () => setPickupContact(flyerExtracted.contactNumber!),
              },
            ].filter((f) => f.value);

            if (fields.length === 0) return null;

            return (
              <div className="rounded-xl border border-flyer-teal/30 bg-flyer-teal/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-flyer-teal uppercase tracking-widest">
                    Detected from flyer
                  </p>
                  <button
                    onClick={() => setFlyerExtracted(null)}
                    className="text-admin-subtle hover:text-admin-muted text-xs"
                    title="Dismiss"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-2">
                  {fields.map(({ label, value, onUse }) => (
                    <div key={label} className="flex items-start gap-2">
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-admin-subtle w-14 pt-0.5">{label}</span>
                      <span className="flex-1 text-xs text-admin-text/80 break-words">{value}</span>
                      {label !== 'Date' && (
                        <button
                          onClick={onUse}
                          disabled={!canEditStatus}
                          className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-flyer-teal border border-flyer-teal/40 rounded px-1.5 py-0.5 hover:bg-flyer-teal/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Use
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          </div>

          {/* ── Zone Colours ── */}
          {(() => {
            const disabled = !canEditStatus || settingsLoading || settingsSaving;

            // All available colour sources, deduped by hex
            const allSources: { hex: string; label: string; group: string }[] = [];
            const seen = new Set<string>();
            const addSwatch = (hex: string, label: string, group: string) => {
              const k = hex.toLowerCase();
              if (!seen.has(k)) { seen.add(k); allSources.push({ hex, label, group }); }
            };
            addSwatch(themeColors.dark,   'Dark',      'Detected');
            addSwatch(themeColors.cream,  'Cream',     'Detected');
            addSwatch(themeColors.teal,   'Primary',   'Detected');
            addSwatch(themeColors.orange, 'Warm',      'Detected');
            addSwatch(themeColors.red,    'Action',    'Detected');
            addSwatch(themeColors.yellow, 'Highlight', 'Detected');
            extendedColors.forEach((hex, i) => addSwatch(hex, `Flyer ${i + 1}`, 'Flyer'));
            // Harmonics computed from home accent (gives options in harmony with home)
            generateHarmonics(zoneColors.home.accent).forEach(({ label: l, hex }) => addSwatch(hex, l, 'Generated'));
            // Harmonics from menu accent (gives contrast options for menu)
            generateHarmonics(zoneColors.menu.accent).forEach(({ label: l, hex }) => addSwatch(hex, l, 'Generated'));

            const setRole = (zone: keyof ZoneColors, role: 'bg' | 'accent' | 'text', hex: string) => {
              setZoneColors(prev => ({ ...prev, [zone]: { ...prev[zone], [role]: hex } }));
              setZoneDirty(true);
            };

            // Swatch picker — inline strip of dots
            const Picker = ({ zone, role }: { zone: keyof ZoneColors; role: 'bg' | 'accent' | 'text' }) => {
              const current = zoneColors[zone][role] ?? '#ffffff';
              // Bias swatch list by role: bg = darker, text = lighter, accent = all
              const swatches = role === 'bg'
                ? allSources.filter(s => { try { const [,,l] = hexToHsl(s.hex); return l < 0.75; } catch { return true; } })
                : role === 'text'
                  ? allSources.filter(s => { try { const [,,l] = hexToHsl(s.hex); return l > 0.35; } catch { return true; } })
                  : allSources;
              return (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Current selection — larger */}
                  <label title="Custom colour" className="shrink-0 relative">
                    <div className="w-8 h-8 rounded-lg border-2 border-admin-text shadow-sm overflow-hidden cursor-pointer"
                      style={{ backgroundColor: current }}>
                      <input type="color" value={current} disabled={disabled}
                        onChange={e => setRole(zone, role, e.target.value)}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer disabled:cursor-not-allowed" />
                    </div>
                  </label>
                  <span className="text-[9px] font-mono text-admin-subtle shrink-0 w-[52px]">{current}</span>
                  {/* All options */}
                  {swatches.map(({ hex, label: tip, group }) => (
                    <button key={hex} title={`${group}: ${tip} ${hex}`} disabled={disabled}
                      onClick={() => setRole(zone, role, hex)}
                      className={`w-5 h-5 rounded-md border transition-all hover:scale-125 disabled:cursor-not-allowed ${current.toLowerCase() === hex.toLowerCase() ? 'border-admin-text scale-125 shadow-md' : 'border-white/40 hover:border-admin-border'}`}
                      style={{ backgroundColor: hex }} />
                  ))}
                </div>
              );
            };

            return (
              <div className="rounded-xl border border-admin-border bg-white/60 p-4 space-y-4">

                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-admin-text">Zone Colours</h3>
                    <p className="text-xs text-admin-subtle">
                      Choose background and accent colours for the Home screen and Menu drawer. Upload a flyer to auto-detect options.
                    </p>
                  </div>
                  <button onClick={() => void handleZoneColorsSave()} disabled={disabled || !zoneDirty}
                    className={`shrink-0 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                      zoneDirty ? 'border-flyer-teal bg-flyer-teal/10 text-admin-text hover:bg-flyer-teal/20'
                               : 'border-admin-border bg-white text-admin-subtle'
                    } disabled:cursor-not-allowed`}>
                    {settingsSaving ? 'Saving…' : zoneDirty ? 'Save' : 'Saved ✓'}
                  </button>
                </div>

                {/* ── Live split preview ── */}
                <div className="grid grid-cols-2 gap-2 select-none">

                  {/* HOME preview */}
                  <div className="rounded-xl overflow-hidden border-2 border-admin-border text-[10px] shadow-sm">
                    {/* Top accent bar */}
                    <div className="px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: zoneColors.home.accent }}>
                      <span className="font-bold uppercase tracking-widest text-[11px] leading-none"
                        style={{ color: zoneColors.home.text }}>ONE SUNDAY</span>
                      <span style={{ color: zoneColors.home.text, opacity: 0.7 }} className="text-[9px]">Rivonia</span>
                    </div>
                    {/* Body */}
                    <div className="px-3 py-2.5 space-y-1.5" style={{ backgroundColor: zoneColors.home.bg }}>
                      {/* Tab nav mockup */}
                      <div className="flex gap-1 mb-2">
                        {['Home', 'Menu'].map(t => (
                          <div key={t} className="px-2 py-0.5 rounded text-[9px] font-semibold"
                            style={{ backgroundColor: t === 'Home' ? zoneColors.home.accent : 'transparent',
                                     color: t === 'Home' ? zoneColors.home.text : 'rgba(255,255,255,0.4)',
                                     border: t === 'Menu' ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>{t}</div>
                        ))}
                      </div>
                      {/* Menu items wireframe */}
                      {['Smoked Brisket', 'Smoked Ribs'].map(name => (
                        <div key={name} className="flex items-center justify-between rounded-lg px-2 py-1.5"
                          style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                          <span style={{ color: 'rgba(255,255,255,0.75)' }} className="truncate">{name}</span>
                          <span className="font-bold shrink-0 ml-1"
                            style={{ color: zoneColors.home.accent }}>+ Add</span>
                        </div>
                      ))}
                    </div>
                    <div className="px-2 py-0.5 text-center text-[9px] font-semibold uppercase tracking-widest"
                      style={{ backgroundColor: zoneColors.home.accent, color: zoneColors.home.text, opacity: 0.6 }}>
                      Home
                    </div>
                  </div>

                  {/* MENU DRAWER preview */}
                  <div className="rounded-xl overflow-hidden border-2 border-admin-border text-[10px] shadow-sm">
                    {/* Drawer header */}
                    <div className="px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: zoneColors.menu.accent }}>
                      <span className="font-bold uppercase tracking-widest text-[11px] leading-none"
                        style={{ color: zoneColors.menu.text }}>THE MENU</span>
                      <div className="w-5 h-5 rounded border flex items-center justify-center text-[9px] font-bold bg-white/20"
                        style={{ color: zoneColors.menu.text, borderColor: zoneColors.menu.text + '40' }}>✕</div>
                    </div>
                    {/* Drawer body */}
                    <div className="px-3 py-2.5 space-y-1.5" style={{ backgroundColor: zoneColors.menu.bg }}>
                      {['Smoked Brisket  R 180', 'Smoked Ribs  R 150'].map(row => (
                        <div key={row} className="flex items-center justify-between rounded-lg px-2 py-1.5"
                          style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                          <span style={{ color: 'rgba(255,255,255,0.8)' }} className="truncate">{row.split('  ')[0]}</span>
                          <span className="font-bold shrink-0 ml-1" style={{ color: zoneColors.menu.accent }}>{row.split('  ')[1]}</span>
                        </div>
                      ))}
                    </div>
                    {/* Cart bar */}
                    <div className="px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: zoneColors.menu.accent }}>
                      <span style={{ color: zoneColors.menu.text }} className="font-semibold">Total  R 330</span>
                      <div className="px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider"
                        style={{ backgroundColor: zoneColors.menu.text, color: zoneColors.menu.accent }}>Checkout</div>
                    </div>
                  </div>
                </div>

                {/* ── Config table — rows: role, cols: Home / Menu ── */}
                <div className="rounded-lg border border-admin-border overflow-hidden text-[11px]">

                  {/* Column headers */}
                  <div className="grid grid-cols-[72px_1fr_1fr] bg-admin-text/4 border-b border-admin-border">
                    <div className="px-3 py-2 text-[9px] font-semibold uppercase tracking-widest text-admin-subtle" />
                    <div className="px-3 py-2 font-bold text-admin-text/70 border-l border-admin-border flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: zoneColors.home.accent }} />Home
                    </div>
                    <div className="px-3 py-2 font-bold text-admin-text/70 border-l border-admin-border flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: zoneColors.menu.accent }} />Menu
                    </div>
                  </div>

                  {/* Background row */}
                  <div className="grid grid-cols-[72px_1fr_1fr] border-b border-admin-border">
                    <div className="px-3 py-3 text-[10px] font-semibold text-admin-subtle self-start pt-3.5">Background</div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="home" role="bg" />
                    </div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="menu" role="bg" />
                    </div>
                  </div>

                  {/* Accent row */}
                  <div className="grid grid-cols-[72px_1fr_1fr] border-b border-admin-border">
                    <div className="px-3 py-3 text-[10px] font-semibold text-admin-subtle self-start pt-3.5">Accent</div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="home" role="accent" />
                    </div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="menu" role="accent" />
                    </div>
                  </div>

                  {/* Text row */}
                  <div className="grid grid-cols-[72px_1fr_1fr]">
                    <div className="px-3 py-3 text-[10px] font-semibold text-admin-subtle self-start pt-3.5">Text</div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="home" role="text" />
                    </div>
                    <div className="px-3 py-3 border-l border-admin-border">
                      <Picker zone="menu" role="text" />
                    </div>
                  </div>
                </div>

                {/* Colour source legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[9px] text-admin-subtle">
                  {extendedColors.length > 0
                    ? <span><span className="inline-block w-2 h-2 rounded-sm bg-flyer-teal/40 mr-1" />{extendedColors.length} colours auto-detected from flyer</span>
                    : <span>Upload a flyer to auto-detect colours</span>
                  }
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-admin-accent/40 mr-1" />Generated harmonics</span>
                  <span>Large dot = custom picker</span>
                </div>
              </div>
            );
          })()}

          {/* ── Menu Items ── */}
          <div className="rounded-xl border border-admin-border bg-white/60 p-4 space-y-2">
            {/* Header row */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-admin-text">Menu Items</h3>
                <p className="text-xs text-admin-subtle">Leave empty to use the built-in menu.</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => { setMenuItemsDraft((prev) => [...prev, { id: crypto.randomUUID(), name: '', price: 0, description: '' }]); setMenuDirty(true); }}
                  disabled={!canEditStatus}
                  className="px-2.5 py-1 rounded-lg border border-admin-border bg-white text-xs font-semibold text-admin-text hover:bg-admin-accent-soft disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  + Add
                </button>
                <button
                  onClick={() => void handleMenuItemsSave()}
                  disabled={!canEditStatus || settingsLoading || settingsSaving || !menuDirty}
                  className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                    menuDirty
                      ? 'border-flyer-teal bg-flyer-teal/10 text-admin-text hover:bg-flyer-teal/20'
                      : 'border-admin-border bg-white text-admin-subtle'
                  } disabled:cursor-not-allowed`}
                >
                  {settingsSaving ? 'Saving…' : menuDirty ? 'Save' : 'Saved ✓'}
                </button>
              </div>
            </div>

            {menuItemsDraft.length === 0 ? (
              <p className="text-xs text-admin-subtle italic py-1">No items — using built-in menu.</p>
            ) : (
              <div className="space-y-0.5">
                {/* Column headers */}
                <div className="grid items-center gap-1.5 px-1" style={{ gridTemplateColumns: '1fr 60px 2fr 24px' }}>
                  <span className="text-[10px] uppercase tracking-wide text-admin-subtle">Name</span>
                  <span className="text-[10px] uppercase tracking-wide text-admin-subtle">Price</span>
                  <span className="text-[10px] uppercase tracking-wide text-admin-subtle">Description</span>
                  <span />
                </div>
                {menuItemsDraft.map((item, idx) => (
                  <div key={item.id} className="grid items-center gap-1.5" style={{ gridTemplateColumns: '1fr 60px 2fr 24px' }}>
                    <input
                      type="text"
                      placeholder="Name"
                      value={item.name}
                      onChange={(e) => { setMenuItemsDraft((prev) => prev.map((it, i) => i === idx ? { ...it, name: e.target.value } : it)); setMenuDirty(true); }}
                      disabled={!canEditStatus}
                      className="w-full px-2 py-1 text-xs rounded border border-admin-border bg-white text-admin-text placeholder-flyer-dark/25 focus:outline-none focus:border-flyer-teal disabled:opacity-60"
                    />
                    <input
                      type="number"
                      placeholder="0"
                      min={0}
                      value={item.price}
                      onChange={(e) => { setMenuItemsDraft((prev) => prev.map((it, i) => i === idx ? { ...it, price: Number(e.target.value) } : it)); setMenuDirty(true); }}
                      disabled={!canEditStatus}
                      className="w-full px-2 py-1 text-xs rounded border border-admin-border bg-white text-admin-text placeholder-flyer-dark/25 focus:outline-none focus:border-flyer-teal disabled:opacity-60"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => { setMenuItemsDraft((prev) => prev.map((it, i) => i === idx ? { ...it, description: e.target.value } : it)); setMenuDirty(true); }}
                      disabled={!canEditStatus}
                      className="w-full px-2 py-1 text-xs rounded border border-admin-border bg-white text-admin-text placeholder-flyer-dark/25 focus:outline-none focus:border-flyer-teal disabled:opacity-60"
                    />
                    <button
                      onClick={() => { setMenuItemsDraft((prev) => prev.filter((_, i) => i !== idx)); setMenuDirty(true); }}
                      disabled={!canEditStatus}
                      className="w-6 h-6 flex items-center justify-center rounded text-admin-subtle hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm leading-none"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Typography ── */}
          <div className="rounded-xl border border-admin-border bg-white/60 p-4 space-y-3">
            {/* Header row */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-admin-text">Typography</h3>
                <p className="text-xs text-admin-subtle">Pick display &amp; body fonts. Auto-detected from flyer.</p>
              </div>
              <button
                onClick={() => void handleFontConfigSave()}
                disabled={!canEditStatus || settingsLoading || settingsSaving || !fontDirty}
                className={`shrink-0 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                  fontDirty
                    ? 'border-flyer-teal bg-flyer-teal/10 text-admin-text hover:bg-flyer-teal/20'
                    : 'border-admin-border bg-white text-admin-subtle'
                } disabled:cursor-not-allowed`}
              >
                {settingsSaving ? 'Saving…' : fontDirty ? 'Save' : 'Saved ✓'}
              </button>
            </div>

            {/* Display font chips */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-admin-subtle mb-1.5">Display / Headings</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {DISPLAY_FONTS.map(({ name, label }) => (
                  <button
                    key={name}
                    onClick={() => {
                      const next = { ...fontConfig, display: name };
                      setFontConfig(next);
                      applyFontConfig(next);
                      setFontDirty(true);
                    }}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    title={name}
                    style={{ fontFamily: `'${name}', serif` }}
                    className={`px-3 py-1.5 rounded-lg border text-sm whitespace-nowrap transition-colors flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed ${
                      fontConfig.display === name
                        ? 'border-flyer-teal bg-flyer-teal/10 text-admin-text font-semibold'
                        : 'border-admin-border bg-white text-admin-text/70 hover:border-admin-border'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body font chips */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-admin-subtle mb-1.5">Body Text</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {BODY_FONTS.map(({ name, label }) => (
                  <button
                    key={name}
                    onClick={() => {
                      const next = { ...fontConfig, body: name };
                      setFontConfig(next);
                      applyFontConfig(next);
                      setFontDirty(true);
                    }}
                    disabled={!canEditStatus || settingsLoading || settingsSaving}
                    title={name}
                    style={{ fontFamily: `'${name}', sans-serif` }}
                    className={`px-3 py-1.5 rounded-lg border text-sm whitespace-nowrap transition-colors flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed ${
                      fontConfig.body === name
                        ? 'border-flyer-teal bg-flyer-teal/10 text-admin-text font-semibold'
                        : 'border-admin-border bg-white text-admin-text/70 hover:border-admin-border'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Live font preview */}
            <div className="rounded-xl overflow-hidden border border-admin-border" style={{ backgroundColor: themeColors.cream }}>
              <div className="px-4 py-2.5" style={{ backgroundColor: themeColors.teal }}>
                <p className="text-white text-base uppercase tracking-widest" style={{ fontFamily: `'${fontConfig.display}', serif` }}>
                  One Sunday
                </p>
              </div>
              <div className="px-4 py-3 space-y-1">
                <p className="text-sm font-semibold" style={{ color: themeColors.dark, fontFamily: `'${fontConfig.display}', serif` }}>
                  Grilled Corn · R 65
                </p>
                <p className="text-xs" style={{ color: themeColors.dark, opacity: 0.65, fontFamily: `'${fontConfig.body}', sans-serif` }}>
                  Charred with salted butter and lime. A favourite since day one.
                </p>
              </div>
            </div>
          </div>
          </div>

        )}



      </main>







      <OrderDrawer



        order={selectedOrder}



        onClose={() => setSelectedOrder(null)}



        onUpdateStatus={canEditStatus ? handleStatusUpdate : undefined}



        busy={busy}



      />

      <CustomerEmailModal
        open={emailModalOpen}
        scope={emailScope}
        onClose={() => setEmailModalOpen(false)}
        onSent={() => void loadOrders()}
      />



    </div>



  );



};







export default AdminApp;
